export const JOB_TYPE = {
  DF: "df",
  DS: "ds",
  DI: "di",
  DB: "db",
  DG: "dg"
};

export const PLATFORM = {
  ALL: "all",
  WINDOWS: "windows",
  MAC: "mac",
  LINUX: "linux"
};

export const JOB_STATUS = {
  COMPLETED: 0,
  RELEASED: 100,
  PROCESSING: 2,
  ERROR: 99,
  CANCELLED: 7,
  DRYRUN: 5
};

export const VALIDATION_STATUS = {
  NOT_VALIDATED: 0,
  IN_VALIDATION: 1,
  VALIDATED: 2,
  SKIPPED: 98,
  FAILED: 99,
  NOT_REQUIRED: 100
};

// Release modes reported by the jobs API that do not permit creating a release from the portal.
export const RELEASE_MODE_BLOCKED = ["disabled"] as const;

export const JOB_CONTENT = {
  zeroStateContent: "There are currently no jobs that match you're search query.",
  publishingPlatformHome: "Publishing Platform Home",
  copyJob: "Copy link to clipboard.",
  dbJobExplainContent: "In order to test your build you will need to use all download links.",
  dbBetaJobExplainContent: "In order to test your build you will need to use all beta links.",
  dbLogExplainContent: (date: any) => `These links wil expire on ${date} (14 days after creation)`,
  releaseBlockedMessage: "This job's channel config does not permit release."
};

export const Patterns = {
  s3SourcePathPattern1: /^(s3|S3):\/\//, // S3://
  s3SourcePathPattern2: /^(s3|S3):\\\\/, // S3:\\
  // alias for Apr callers
  sourcePath: /^(s3|S3):\/\//
};

export const TARGET_DOMAIN_OPTIONS = [
  { children: "csme.autodesk.com", value: "https://csme.autodesk.com" },
  { children: "technicaladvisory-cdn.autodesk.com", value: "https://technicaladvisory-cdn.autodesk.com" }
].sort((a, b) => a.children.localeCompare(b.children));

export const TARGET_DOMAIN_PLCS: string[] = ["WKFLADV"];

// Used by SUBMIT_JOB_CONTENT.publishing; consumers should import PUBLISHING from commonConstants
const PUBLISHING_CONST: Record<string, string> = {
  PRODUCT: "Product",
  PRODUCT_MAJOR_UPGRADE: "Product (Major Upgrade)",
  BADGE_ICON: "Badge/Icon",
  UPDATE: "Update",
  COMPONENT: "Component",
  EXTENSION: "Extension",
  LIBRARY: "Library",
  FILE: "File (release notes, etc...)",
  ENGINE: "Engine",
  LANGUAGE_PACK: "Language Pack",
  PLUG_IN: "Plug-in"
};

export const SUBMIT_JOB_CONTENT = {
  // https://wiki.autodesk.com/display/WGMN/%5BXD%5D+Autodesk+Publishing+Platform+%7C+Job+Creation
  tooltip: {
    // Get started
    publish: {
      message: "Select the type of job you would like to publish.",
      url: "https://wiki.autodesk.com/display/WGMN/Job+types+supported+by+APP"
    },
    yaml: {
      message: "Add a properly formed job request .yaml for this job.",
      url: "https://wiki.autodesk.com/display/WGMN/Job+Request+YAML+2.0+Schema"
    },
    componentUPI2: "Component UPI 2 that corresponds with the component with this update.",
    componentUPI2Url: "https://wiki.autodesk.com/display/WGMN/How+to+Publish+a+Component+and+Component+Updates",
    // Job profile
    teamName: "Add the unique identifier of your team.",
    dryRun: {
      message: "Is this is a test or publishing to production.",
      url: "https://wiki.autodesk.com/display/WGMN/Job+Info+Section"
    },
    // Product info
    displayName: {
      message: "Enter the name as it will appear to Autodesk customers.",
      url: "https://wiki.autodesk.com/display/WGMN/Product+Info+Section"
    },
    partNumber: {
      message: "Enter the valid serial number exactly the way it is stored in SAP.",
      url: "https://wiki.autodesk.com/display/WGMN/Product+Info+Section"
    },
    // Image info
    releaseVersion: {
      message: "Enter the release year or version.",
      url: "https://wiki.autodesk.com/display/WGMN/Image+Info+Section"
    },
    sourcePath: {
      message: "Enter the path of the directory where the source file is located.",
      url: "https://wiki.autodesk.com/display/WGMN/Image+Info+Section"
    },
    sourceRoot: {
      message: "Enter the top level directory where the source file is located.",
      url: "https://wiki.autodesk.com/display/WGMN/Image+Info+Section"
    },
    rootManifestPath: {
      message: "Enter the relative path of the root manifest (.xml) file inside the source zip.",
      url: "https://wiki.autodesk.com/display/WGMN/Image+Info+Section"
    },
    imageFileName: {
      message: "What would you like to name this file.",
      url: "https://wiki.autodesk.com/display/WGMN/Product+Info+Section"
    },
    outputPath: "Enter your CDN path.",
    targetDomain: {
      message: "The domain where this file will be published."
    }
  },
  publishing: Object.keys(PUBLISHING_CONST)
    .map((key) => ({ children: PUBLISHING_CONST[key], value: PUBLISHING_CONST[key] }))
    .sort((a, b) => a.children.localeCompare(b.children)),
  // "USA"/"en-US" below pin the historical defaults (see ProductInfo.jsx / redux/jobs/submitJob.tsx) —
  // sorting these lists must not change which option is selected by default.
  defaultCountry: "USA",
  defaultLanguage: "en-US",
  itemInfo: {
    noProfileFoundForItem: (plc: string, releaseType: string, platform: string) =>
      `No profile found for ${plc} ${platform.charAt(0).toUpperCase() + platform.slice(1)} ${releaseType}.`,
    systemDefaultProfileReview: "-"
  },
  countries: [
    { children: "USA", value: "USA" },
    { children: "UNITED KINGDOM", value: "UNITED KINGDOM" },
    { children: "SINGAPORE", value: "SINGAPORE" },
    { children: "ROMANIA", value: "ROMANIA" },
    { children: "POLAND", value: "POLAND" },
    { children: "CHINA", value: "CHINA" },
    { children: "CANADA", value: "CANADA" },
    { children: "AUSTRALIA", value: "AUSTRALIA" }
  ].sort((a, b) => a.children.localeCompare(b.children)),
  imageTypes: [
    { children: "Bundle", value: "bundle" },
    { children: "File", value: "file" },
    { children: "Package", value: "package" },
    { children: "Update", value: "update" }
  ],
  languages: [
    { children: "en-US - English - United States", value: "en-US", shortName: "english_us" },
    { children: "zh-Hant - Traditional Chinese", value: "zh-TW", shortName: "traditional_chinese" },
    { children: "zh-Hans - Simplified Chinese", value: "zh-CN", shortName: "simplified_chinese" },
    { children: "ru - Russian", value: "ru-RU", shortName: "russian" },
    { children: "ro - Romanian", value: "ro-RO", shortName: "romanian" },
    { children: "pt-PT - Portuguese - European", value: "pt-PT", shortName: "portuguese_european" },
    { children: "pt-BR - Portuguese - Brazilian", value: "pt-BR", shortName: "portuguese_brazilian" },
    { children: "pl - Polish", value: "pl-PL", shortName: "polish" },
    { children: "nl - Dutch", value: "nl-NL", shortName: "dutch" },
    { children: "NA - Language Independent", value: "na", shortName: "NA" },
    { children: "ko - Korean", value: "ko-KR", shortName: "korean" },
    { children: "ja - Japanese", value: "ja-JP", shortName: "japanese" },
    { children: "it - Italian", value: "it-IT", shortName: "italian" },
    { children: "hu - Hungarian", value: "hu-HU", shortName: "hungarian" },
    { children: "fr-CA - Canadian French", value: "fr-CA", shortName: "french_canadian" },
    { children: "fr - French", value: "fr-FR", shortName: "french" },
    { children: "es - Spanish", value: "es-ES", shortName: "spanish" },
    { children: "en-IN - English - India", value: "en-IN", shortName: "english_india" },
    { children: "en-GB - English - Great Britain", value: "en-GB", shortName: "english_great_britain" },
    { children: "en-AU - English - Australia", value: "en-AU", shortName: "english_australia" },
    { children: "en - English - International", value: "en-EN", shortName: "english" },
    { children: "de - German", value: "de-DE", shortName: "german" },
    { children: "cs - Czech", value: "cs-CZ", shortName: "czech" }
  ].sort((a, b) => a.children.localeCompare(b.children)),
  milestone: [
    { value: "Alpha", children: "Alpha" },
    { value: "Beta", children: "Beta" },
    { value: "RTM", children: "RTM" },
    { value: "Pre-Release", children: "Pre-Release" },
    { value: "Other", children: "Other" },
    { value: "Slipstream", children: "Slipstream" },
    { value: "Upgrade Release", children: "Upgrade Release" }
  ].sort((a, b) => a.children.localeCompare(b.children)),
  errors: {
    errorTitle1: "Job Creation Error (400)",
    errorTitle2: "Job Creation Error (500)",
    errorTitle3: "Job Creation Error",
    saveTitle: "Job Creation not saved",
    warningTitle: "Job Creation Warning",
    republishCancelTitle: "Job Republish Cancel",
    republishErrorTitle: "Job Republish Error",
    successTitle: "Success!",
    successContent: (jobID: any) => `Job ID: ${jobID} has been created successfully. It is queued and can be found in your dashboard.`,
    errorContent1: "An unexpected error occurred while trying to create the job. Please try again later.",
    errorContent2: (jobID: any) =>
      `Job ID: ${jobID} already exists for Download (Browser Download/ Beta Download) image. Autodesk Publishing Platform does not allow publishing of duplicate jobs. The job to create Install(Install Now/DMC/CustomInstall) image failed. Please wait a few minutes and try again.`,
    errorContent3: (jobID: any) =>
      `Autodesk Publishing Platform encountered an error while creating Job ID: ${jobID} the Install (Install Now/DMC/CustomInstall) was created successfully. The image for Download (Browser Download/ Beta Download) image failed please make sure all fields are filled correctly and try again.`,
    errorContent4: (jobID: any) =>
      `Job ID: ${jobID} already exists for Install(Install Now/DMC/CustomInstall) image, Autodesk Publishing Platform does not allow publishing of duplicate jobs. The Download (Browser Download/ Beta Download) image failed. Please make sure all fields are filled correctly and try again. `,
    errorContent5: (jobID: any) =>
      `Autodesk Publishing Platform encountered an error while creating Job ID: ${jobID} the image for Install (Install Now/DMC/CustomInstall) image failed please make sure all fields are filled correctly and try again. The image for Download (Browser Download/ Beta Download) was created successfully. Please make sure all fields are filled correctly and try again. `,
    errorContent6: "Job creation failed, please make sure all fields are filled correctly and try again. ",
    errorContent7: "Job creation failed, please provide a valid source path.",
    errorContent8: "Job is already published, please try again with a different job details.",
    duplicateContent1: (jobID: any, goToJob?: any) => (
      <>
        Job ID:{" "}
        <b>
          <u style={{ cursor: "pointer" }} onClick={() => goToJob(jobID)}>
            {jobID}
          </u>
        </b>{" "}
        already exists, Autodesk Publishing Platform does not allow publishing of duplicate jobs.
      </>
    ),
    duplicateContent2: (jobID: any, goToJob?: any) => (
      <>
        Job ID:{" "}
        <b>
          <u style={{ cursor: "pointer" }} onClick={() => goToJob(jobID)}>
            {jobID}
          </u>
        </b>{" "}
        already exists for Download (Browser Download/ Beta Download) image, Autodesk Publishing Platform does not allow publishing of duplicate jobs.
        You have successfully created Install (Install Now/DMC/CustomInstall) image.
      </>
    ),
    duplicateContent3: (jobID: any, goToJob?: any) => (
      <>
        Job ID:{" "}
        <b>
          <u style={{ cursor: "pointer" }} onClick={() => goToJob(jobID)}>
            {jobID}
          </u>
        </b>{" "}
        already exists for Install(Install Now/DMC/CustomInstall) image, Autodesk Publishing Platform does not allow publishing of duplicate jobs. You
        have successfully created Download (Browser Download/ Beta Download) image.
      </>
    ),
    saveContent: "Are you sure you want to exit job creation without saving it?",
    republishCancelContent: "Are you sure you want to cancel without republishing job?",
    republishErrorContent: "Job republish failed, please make sure all fields are filled correctly and try again.",
    republishSuccessContent: (jobID: any) => `Job ID: ${jobID} has been republished successfully.`
  },
  publish: {
    publishSuccessTitle: "Job Submit Success",
    publishSuccessContent: (jobID: any) => `Job ID: ${jobID} has been submitted for publishing successfully.`
  },
  release: {
    releaseErrorContent: (type: string) =>
      `The release is currently on hold because the ${type} has not passed validation. Please wait until the image is successfully validated before proceeding with the release.`
  },
  revalidate: {
    revalidateTitle: "Job Revalidation",
    revalidateContent: (jobID: string, message: string) => `${jobID ? `Job ID: ${jobID} - ` : ""} ${message ? `${message}` : ""}`
  },
  skipValidation: {
    content: (imageTypes: string[]) =>
      `APP could not validate ${imageTypes.length ? imageTypes.map((imageType: string) => (imageType === "di" ? "Install Image" : "Download Image")).join(", ") : "this image"
      }. Please verify it is installable and downloadable before proceeding with release.`
  }
};
